import { getTranslation, type MivoTranslationType } from "@mivo/i18n";

export type ChatScheduleDay = {
    dayOfWeek: number;
    enabled: boolean;
    startTime: string;
    endTime: string;
};

export type ChatScheduleResponse = {
    restricted: boolean;
    days: ChatScheduleDay[];
};

export const CHAT_SCHEDULE_DAY_TRANSLATIONS: Record<number, MivoTranslationType> = {
    0: {
        "pt-BR": "Domingo",
        en: "Sunday",
        es: "Domingo",
        de: "Sonntag",
    },
    1: {
        "pt-BR": "Segunda-feira",
        en: "Monday",
        es: "Lunes",
        de: "Montag",
    },
    2: {
        "pt-BR": "Terça-feira",
        en: "Tuesday",
        es: "Martes",
        de: "Dienstag",
    },
    3: {
        "pt-BR": "Quarta-feira",
        en: "Wednesday",
        es: "Miércoles",
        de: "Mittwoch",
    },
    4: {
        "pt-BR": "Quinta-feira",
        en: "Thursday",
        es: "Jueves",
        de: "Donnerstag",
    },
    5: {
        "pt-BR": "Sexta-feira",
        en: "Friday",
        es: "Viernes",
        de: "Freitag",
    },
    6: {
        "pt-BR": "Sábado",
        en: "Saturday",
        es: "Sábado",
        de: "Samstag",
    },
};

/** @deprecated Prefer getChatScheduleDayLabel — kept for gradual migration. */
export const CHAT_SCHEDULE_DAY_LABELS: Record<number, string> = {
    0: "Domingo",
    1: "Segunda-feira",
    2: "Terça-feira",
    3: "Quarta-feira",
    4: "Quinta-feira",
    5: "Sexta-feira",
    6: "Sábado",
};

export function getChatScheduleDayLabel(dayOfWeek: number): string {
    const translation = CHAT_SCHEDULE_DAY_TRANSLATIONS[dayOfWeek] ?? CHAT_SCHEDULE_DAY_TRANSLATIONS[0]!;
    return getTranslation(translation);
}

export const WEEKDAY_INDICES = [1, 2, 3, 4, 5];
export const WEEKEND_INDICES = [0, 6];
export const ALL_DAY_INDICES = [0, 1, 2, 3, 4, 5, 6];

export const CHAT_SCHEDULE_DEFAULT_START = "06:00";
export const CHAT_SCHEDULE_DEFAULT_END = "23:00";

const CHAT_TIME_MIN = 6 * 60;
const CHAT_TIME_MAX = 23 * 60;

/** Horários 06:00–23:00, passo de 5 minutos. */
export function buildChatTimeOptions(): string[] {
    const options: string[] = [];
    for (let m = CHAT_TIME_MIN; m <= CHAT_TIME_MAX; m += 5) {
        const h = Math.floor(m / 60);
        const min = m % 60;
        options.push(`${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`);
    }
    return options;
}

export function defaultChatScheduleDays(): ChatScheduleDay[] {
    return Array.from({ length: 7 }, (_, dayOfWeek) => ({
        dayOfWeek,
        enabled: false,
        startTime: CHAT_SCHEDULE_DEFAULT_START,
        endTime: CHAT_SCHEDULE_DEFAULT_END,
    }));
}

export function hasAnyChatDayEnabled(days: ChatScheduleDay[]): boolean {
    return days.some((d) => d.enabled);
}

export function chatTimeToMinutes(time: string): number {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
}

/** Opções de início: estritamente antes do término. */
export function getChatStartTimeOptions(allOptions: string[], endTime: string): string[] {
    const endMinutes = chatTimeToMinutes(endTime);
    return allOptions.filter((t) => chatTimeToMinutes(t) < endMinutes);
}

/** Opções de término: estritamente depois do início. */
export function getChatEndTimeOptions(allOptions: string[], startTime: string): string[] {
    const startMinutes = chatTimeToMinutes(startTime);
    return allOptions.filter((t) => chatTimeToMinutes(t) > startMinutes);
}

export function nextChatEndAfterStart(startTime: string, allOptions: string[]): string {
    const endOptions = getChatEndTimeOptions(allOptions, startTime);
    return endOptions[0] ?? allOptions[allOptions.length - 1]!;
}

export function normalizeChatDayTimes(day: ChatScheduleDay, allOptions: string[]): ChatScheduleDay {
    if (chatTimeToMinutes(day.startTime) >= chatTimeToMinutes(day.endTime)) {
        return { ...day, endTime: nextChatEndAfterStart(day.startTime, allOptions) };
    }
    return day;
}

export function applyChatScheduleRestriction(days: ChatScheduleDay[], restricted: boolean): ChatScheduleDay[] {
    if (!restricted) {
        return days.map((d) => ({ ...d, enabled: false }));
    }
    return days.map((d) => ({
        ...d,
        enabled: true,
        startTime: CHAT_SCHEDULE_DEFAULT_START,
        endTime: CHAT_SCHEDULE_DEFAULT_END,
    }));
}
